import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import {
  AiOutlineFileDone,
  AiOutlineUser,
  AiOutlineSetting,
  AiOutlineLogout,
  AiOutlineHeart,
  AiOutlineShoppingCart,
  AiOutlineMenu,
  AiOutlineClose,
} from 'react-icons/ai';
import { IoAccessibilityOutline } from 'react-icons/io5';
import AccessibilitySettings from './AccessibilitySettings';
import './Header.css';

const iconSize = 20;

function Header() {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [storeSettings, setStoreSettings] = useState(null);
  const [a11yOpen, setA11yOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const langDropdownRef = React.useRef(null);
  const langDropdownCloseTimer = React.useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const openLangDropdown = () => {
    if (langDropdownCloseTimer.current) {
      clearTimeout(langDropdownCloseTimer.current);
      langDropdownCloseTimer.current = null;
    }
    setLangDropdownOpen(true);
  };
  const closeLangDropdown = () => {
    langDropdownCloseTimer.current = setTimeout(() => setLangDropdownOpen(false), 120);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      apiClient.get('/auth/me')
        .then(res => {
          setUser(res.data);
          if (res.data.preferred_language === 'en' || res.data.preferred_language === 'de') {
            setLanguage(res.data.preferred_language);
          }
        })
        .catch(() => {
          localStorage.removeItem('token');
          setUser(null);
        });
    }

    // Gauti parduotuvės nustatymus
    loadStoreSettings();
  }, [setLanguage]);

  // Naudoti React Query krepšelio kiekiui - automatiškai atnaujins, kai bus invaliduotas 'cart' query
  const { data: cart } = useQuery({
    queryKey: ['cart'],
    queryFn: async () => {
      const sessionId = localStorage.getItem('sessionId');
      try {
        const res = await apiClient.get('/cart', {
          headers: sessionId ? { 'x-session-id': sessionId } : {},
        });
        return res.data;
      } catch (error) {
        // Jei krepšelis tuščias arba klaida, grąžinti tuščią masyvą
        return { items: [] };
      }
    },
    refetchOnWindowFocus: true, // Atnaujinti kai langas gauna focus
    refetchOnMount: true, // Atnaujinti kai komponentas mountinamas
    staleTime: 0, // Visada laikyti stale, kad atnaujintų real-time
    refetchInterval: false, // Nenaudoti automatinio refetch, tik invalidacija
  });

  const { data: headerMenuData } = useQuery({
    queryKey: ['header-menu-public', language],
    queryFn: async () => {
      const res = await apiClient.get('/header-menu/public', { params: { lang: language || 'lt' } });
      return res.data;
    },
    staleTime: 60 * 1000,
  });

  const headerMenuItems = headerMenuData?.content?.items ?? [];
  const useDynamicHeaderMenu = headerMenuItems.length > 0;

  // Apskaičiuoti krepšelio kiekį
  const cartCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  
  // State krepšelio badge animacijai
  const [badgeKey, setBadgeKey] = useState(0);
  
  // Atnaujinti badge key, kai kiekis keičiasi (animacijai)
  useEffect(() => {
    if (cartCount > 0) {
      setBadgeKey(prev => prev + 1);
    }
  }, [cartCount]);

  // Gauti mėgstamų prekių kiekį (tik jei vartotojas prisijungęs)
  const { data: favoritesCount } = useQuery({
    queryKey: ['favorites-count'],
    queryFn: async () => {
      try {
        const res = await apiClient.get('/user/favorites/count');
        return res.data;
      } catch (error) {
        return { count: 0 };
      }
    },
    enabled: !!user, // Tik jei vartotojas prisijungęs
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  // Paslėpti badge ir bet kokius elementus, kurie turi tekstą "0"
  useEffect(() => {
    // Paslėpti badge
    const badges = document.querySelectorAll('.cart-badge, .favorites-badge');
    badges.forEach(badge => {
      if (badge.textContent === '0' || badge.textContent.trim() === '0') {
        badge.style.display = 'none';
      }
    });
    
    // Paslėpti bet kokius elementus nav viduje, kurie turi tik tekstą "0"
    const navElements = document.querySelectorAll('.nav > *');
    navElements.forEach(element => {
      const text = element.textContent?.trim();
      // Jei elementas turi tik "0" (be kitų simbolių ar ikonų)
      if (text === '0' && element.children.length === 0) {
        element.style.display = 'none';
      }
      // Jei elementas turi "0" kaip vienintelį tekstą (be SVG ar kitų elementų)
      if (text === '0') {
        const hasOnlyZero = Array.from(element.childNodes).every(node => {
          if (node.nodeType === Node.TEXT_NODE) {
            return node.textContent.trim() === '0' || node.textContent.trim() === '';
          }
          if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'SVG') {
            return false; // Jei yra SVG, tai ne tik "0"
          }
          return true;
        });
        if (hasOnlyZero && element.children.length === 0) {
          element.style.display = 'none';
        }
      }
    });
    
    // Paslėpti bet kokį elementą tarp account-link ir admin-link, kuris turi tekstą "0"
    const accountLink = document.querySelector('.account-link');
    const adminLink = document.querySelector('.admin-link');
    if (accountLink) {
      // Rasti visus elementus po account-link
      let currentElement = accountLink.nextElementSibling;
      while (currentElement) {
        // Jei pasiekėme admin-link arba btn-icon, sustoti
        if (currentElement.classList.contains('admin-link') || 
            currentElement.classList.contains('btn-icon') ||
            currentElement.classList.contains('favorites-link') ||
            currentElement.classList.contains('cart-link')) {
          break;
        }
        
        const text = currentElement.textContent?.trim();
        // Jei elementas turi tik "0" arba yra span su "0"
        if (text === '0' || (text === '0' && currentElement.children.length === 0)) {
          currentElement.style.display = 'none';
        }
        
        // Patikrinti, ar yra badge arba span su "0"
        const badges = currentElement.querySelectorAll('.cart-badge, .favorites-badge, span, .badge');
        badges.forEach(badge => {
          if (badge.textContent === '0' || badge.textContent.trim() === '0') {
            badge.style.display = 'none';
          }
        });
        
        currentElement = currentElement.nextElementSibling;
      }
      
      // Patikrinti tekstinius node'us
      let textNode = accountLink.nextSibling;
      while (textNode && textNode !== adminLink) {
        if (textNode.nodeType === Node.TEXT_NODE) {
          const text = textNode.textContent?.trim();
          if (text === '0') {
            textNode.textContent = '';
          }
        }
        textNode = textNode.nextSibling;
      }
    }
    
    // Paslėpti bet kokį badge arba skaičių ant admin linko
    if (adminLink) {
      const adminBadges = adminLink.querySelectorAll('.cart-badge, .favorites-badge, span, .badge');
      adminBadges.forEach(badge => {
        if (badge.textContent === '0' || badge.textContent.trim() === '0') {
          badge.style.display = 'none';
        }
      });
      // Paslėpti bet kokį tekstą "0" admin linke
      if ((adminLink.textContent.trim() === '0') || (adminLink.textContent.includes('0') && !adminLink.querySelector('svg'))) {
        // Jei admin linkas turi tik "0" be SVG, paslėpti
        const svg = adminLink.querySelector('svg');
        if (!svg && adminLink.textContent.trim() === '0') {
          adminLink.style.display = 'none';
        }
      }
    }
  }, [cartCount, favoritesCount, user]);

  const loadStoreSettings = async () => {
    try {
      const res = await apiClient.get('/store-settings');
      const settings = res.data;
      // Pašalinti "parduotuvė" iš company_name
      if (settings.company_name) {
        settings.company_name = settings.company_name.replace(/\s*parduotuvė\s*/gi, '').trim();
      }
      // Patikrinti ar logo_url yra teisingas
      if (settings.logo_url && settings.logo_url !== null && settings.logo_url !== undefined) {
        if (!settings.logo_url.startsWith('http') && !settings.logo_url.startsWith('/')) {
          settings.logo_url = '/' + settings.logo_url;
        }
      } else {
        settings.logo_url = null;
      }
      setStoreSettings(settings);
    } catch (error) {
      setStoreSettings({
        company_name: 'Magtic',
        logo_url: null
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" className="logo">
              {storeSettings?.logo_url &&
               storeSettings.logo_url !== null &&
               storeSettings.logo_url !== undefined &&
               String(storeSettings.logo_url).trim() !== '' ? (
                <img
                  src={storeSettings.logo_url}
                  alt={storeSettings.company_name || 'Magtic'}
                  className="logo-image"
                  onLoad={() => {}}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (!e.target.parentNode.querySelector('h1')) {
                      const h1 = document.createElement('h1');
                      h1.textContent = storeSettings?.company_name || 'Magtic';
                      e.target.parentNode.appendChild(h1);
                    }
                  }}
                  style={{ display: 'block' }}
                />
              ) : (
                <h1>{storeSettings?.company_name || 'Magtic'}</h1>
              )}
            </Link>
          </div>

          <div className="header-right-wrapper">
          <div className="header-right-group">
          <nav className="nav-right">
            <button
              type="button"
              className="btn-icon a11y-trigger"
              onClick={() => setA11yOpen(true)}
              title={t('accessibility')}
              aria-label={t('openAccessibility')}
            >
              <IoAccessibilityOutline size={iconSize} aria-hidden />
            </button>
            {user ? (
              <>
                <Link to="/my-account" title={t('myAccount')} className="account-link">
                  <AiOutlineUser size={iconSize} />
                </Link>
                <Link to="/orders" title={t('myOrders')} className="orders-link">
                  <AiOutlineFileDone size={iconSize} />
                </Link>
                <Link to="/my-favorites" className="favorites-link" title={t('myFavorites')}>
                  <AiOutlineHeart size={iconSize} />
                  {Number(favoritesCount?.count) > 0 ? (
                    <span className="favorites-badge">{favoritesCount.count}</span>
                  ) : null}
                </Link>
                <Link to="/cart" className="cart-link" title={t('cart')}>
                  <AiOutlineShoppingCart size={iconSize} />
                  {Number(cartCount) > 0 ? (
                    <span key={badgeKey} className="cart-badge">
                      {cartCount}
                    </span>
                  ) : null}
                </Link>
                {Boolean(user.is_admin) ? (
                  <Link to="/admin" title={t('admin')} className="admin-link">
                    <AiOutlineSetting size={iconSize} />
                  </Link>
                ) : null}
                <button onClick={handleLogout} className="btn-icon" title={t('logout')}>
                  <AiOutlineLogout size={iconSize} />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" title={t('login')} className="login-link">
                  <AiOutlineUser size={iconSize} />
                </Link>
                <Link to="/cart" className="cart-link" title={t('cart')}>
                  <AiOutlineShoppingCart size={iconSize} />
                  {Number(cartCount) > 0 ? (
                    <span key={badgeKey} className="cart-badge">
                      {cartCount}
                    </span>
                  ) : null}
                </Link>
              </>
            )}
          </nav>

          <button
            type="button"
            className="header-mobile-toggle"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? t('close') : t('menu')}
          >
            {mobileMenuOpen ? <AiOutlineClose size={26} /> : <AiOutlineMenu size={26} />}
          </button>
          </div>

          <div className="header-lang-dropdown"
            ref={langDropdownRef}
            onMouseEnter={openLangDropdown}
            onMouseLeave={closeLangDropdown}
          >
            <button
              type="button"
              className="lang-dropdown-trigger"
              title={language === 'lt' ? 'Lietuvių' : language === 'en' ? 'English' : 'Deutsch'}
              aria-expanded={langDropdownOpen}
              aria-haspopup="listbox"
              aria-label={t('language')}
            >
              {t(`lang_${language}`)}
            </button>
            {langDropdownOpen && (
              <ul className="lang-dropdown-menu" role="listbox" aria-label={t('language')}>
                {['de', 'en', 'lt'].map((lang) => (
                  <li key={lang} role="option" aria-selected={language === lang}>
                    <button
                      type="button"
                      className={`lang-dropdown-item ${language === lang ? 'active' : ''}`}
                      onClick={() => {
                        setLanguage(lang);
                        setLangDropdownOpen(false);
                        if (user) {
                          apiClient.put('/user/profile', { preferred_language: lang }).catch(() => {});
                        }
                      }}
                    >
                      {t(`lang_${lang}`)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          </div>

          <nav className="nav-center" aria-label={t('home')}>
            {useDynamicHeaderMenu ? (
              headerMenuItems.map((item) => {
                const href = item.type === 'page' && item.pageSlug ? `/page/${item.pageSlug}` : (item.url || '#');
                const isExternal = href.startsWith('http');
                const label = item.label || href;
                if (isExternal) {
                  return (
                    <a key={item.id} href={href} target="_blank" rel="noopener noreferrer">
                      {label}
                    </a>
                  );
                }
                return (
                  <Link key={item.id} to={href}>
                    {label}
                  </Link>
                );
              })
            ) : (
              <Link to="/products">{t('products')}</Link>
            )}
          </nav>
        </div>
      </div>

      <div className={`header-mobile-menu ${mobileMenuOpen ? 'is-open' : ''}`} aria-hidden={!mobileMenuOpen}>
        <nav className="mobile-nav-links">
          {useDynamicHeaderMenu ? (
            headerMenuItems.map((item) => {
              const href = item.type === 'page' && item.pageSlug ? `/page/${item.pageSlug}` : (item.url || '#');
              const isExternal = href.startsWith('http');
              const label = item.label || href;
              if (isExternal) {
                return (
                  <a key={item.id} href={href} target="_blank" rel="noopener noreferrer" className="mobile-nav-link">
                    {label}
                  </a>
                );
              }
              return (
                <Link key={item.id} to={href} className="mobile-nav-link">
                  {label}
                </Link>
              );
            })
          ) : (
            <>
              <Link to="/" className="mobile-nav-link">{t('home')}</Link>
              <Link to="/products" className="mobile-nav-link">{t('products')}</Link>
            </>
          )}
        </nav>
        <div className="mobile-lang-section">
          <span className="mobile-lang-label">{t('language')}</span>
          <div className="mobile-lang-buttons">
            {['de', 'en', 'lt'].map((lang) => (
              <button
                key={lang}
                type="button"
                className={`mobile-lang-btn ${language === lang ? 'active' : ''}`}
                onClick={() => {
                  setLanguage(lang);
                  if (user) apiClient.put('/user/profile', { preferred_language: lang }).catch(() => {});
                }}
              >
                {t(`lang_${lang}`)}
              </button>
            ))}
          </div>
        </div>
        <nav className="mobile-actions">
          <button type="button" className="mobile-action" onClick={() => { setA11yOpen(true); setMobileMenuOpen(false); }}>
            <IoAccessibilityOutline size={iconSize} />
            <span>{t('accessibility')}</span>
          </button>
          {user ? (
            <>
              <Link to="/my-account" className="mobile-action" onClick={() => setMobileMenuOpen(false)}>
                <AiOutlineUser size={iconSize} />
                <span>{t('myAccount')}</span>
              </Link>
              <Link to="/orders" className="mobile-action" onClick={() => setMobileMenuOpen(false)}>
                <AiOutlineFileDone size={iconSize} />
                <span>{t('myOrders')}</span>
              </Link>
              <Link to="/my-favorites" className="mobile-action" onClick={() => setMobileMenuOpen(false)}>
                <AiOutlineHeart size={iconSize} />
                <span>{t('myFavorites')}</span>
                {Number(favoritesCount?.count) > 0 ? <span className="mobile-badge">{favoritesCount.count}</span> : null}
              </Link>
              <Link to="/cart" className="mobile-action" onClick={() => setMobileMenuOpen(false)}>
                <AiOutlineShoppingCart size={iconSize} />
                <span>{t('cart')}</span>
                {Number(cartCount) > 0 ? <span className="mobile-badge">{cartCount}</span> : null}
              </Link>
              {Boolean(user.is_admin) ? (
                <Link to="/admin" className="mobile-action" onClick={() => setMobileMenuOpen(false)}>
                  <AiOutlineSetting size={iconSize} />
                  <span>{t('admin')}</span>
                </Link>
              ) : null}
              <button type="button" className="mobile-action" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
                <AiOutlineLogout size={iconSize} />
                <span>{t('logout')}</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="mobile-action" onClick={() => setMobileMenuOpen(false)}>
                <AiOutlineUser size={iconSize} />
                <span>{t('login')}</span>
              </Link>
              <Link to="/cart" className="mobile-action" onClick={() => setMobileMenuOpen(false)}>
                <AiOutlineShoppingCart size={iconSize} />
                <span>{t('cart')}</span>
                {Number(cartCount) > 0 ? <span className="mobile-badge">{cartCount}</span> : null}
              </Link>
            </>
          )}
        </nav>
      </div>
      <AccessibilitySettings isOpen={a11yOpen} onClose={() => setA11yOpen(false)} />
    </header>
  );
}

export default Header;
